import React from 'react';
import { Calendar, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { trackButtonClick } from '../utils/gtag';

interface CTAProps {
  title: string;
  subtitle: string;
  primaryButtonText: string;
  primaryButtonHref: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  variant?: 'primary' | 'secondary' | 'accent';
  className?: string;
}

const CTA: React.FC<CTAProps> = ({
  title,
  subtitle,
  primaryButtonText,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonHref,
  variant = 'primary',
  className = ''
}) => {
  const primaryIsExternal = /^https?:\/\//.test(primaryButtonHref);
  const getVariantStyles = () => {
    switch (variant) {
      case 'accent':
        return 'bg-gradient-to-br from-gray-50 via-blue-50/30 to-accent-50/30 relative overflow-hidden';
      case 'secondary':
        return 'bg-gradient-to-br from-gray-50 via-primary-50/30 to-accent-50/30 relative overflow-hidden';
      default:
        return 'bg-gradient-to-br from-gray-50 via-blue-50/30 to-accent-50/30 relative overflow-hidden';
    }
  };

  const getTextStyles = () => {
    switch (variant) {
      case 'accent':
        return 'text-gray-900';
      case 'secondary':
        return 'text-gray-900';
      default:
        return 'text-gray-900';
    }
  };

  return (
    <section className={`py-20 sm:py-24 ${getVariantStyles()} ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/3 to-accent-500/3"></div>
      <div className="container-custom relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-6 ${getTextStyles()}`}>
            {title}
          </h2>
          <p className={`text-lg sm:text-xl mb-8 leading-relaxed ${getTextStyles().replace('text-gray-900', 'text-gray-600')}`}>
            {subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href={primaryButtonHref}
              {...(primaryIsExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              onClick={() => trackButtonClick(primaryButtonText, 'CTA Section')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              {primaryButtonText}
            </a>
            
            {secondaryButtonText && secondaryButtonHref && (
              <a
                href={secondaryButtonHref}
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                {secondaryButtonText}
              </a>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Free consultation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Confidential & safe</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
